import React, { useEffect, useState } from "react";
import { useI18n } from "../i18n/LanguageContext";
import { CaretDown } from "@phosphor-icons/react";

export default function Header({ onCta }) {
  const { t, lang, setLang, languages } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { id: "concept", label: t.nav.concept },
    { id: "problem", label: t.nav.problem },
    { id: "solution", label: t.nav.solution },
    { id: "system", label: t.nav.layers },
    { id: "membership", label: t.nav.membership },
  ];

  const scrollTo = (id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-500 ${
        scrolled ? "bg-ivory/95 border-b border-habitus-border backdrop-blur-sm" : "bg-transparent"
      }`}
    >
      <div className="px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto flex items-center justify-between h-20">
        <button
          data-testid="logo-home"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="font-mono text-sm tracking-[0.35em] uppercase text-charcoal"
        >
          HABITUS
        </button>

        <nav className="hidden lg:flex items-center gap-10">
          {navItems.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-${item.id}`}
              onClick={() => scrollTo(item.id)}
              className="font-mono text-[0.7rem] uppercase tracking-widest text-charcoal/70 hover:text-brass transition-colors"
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          {/* Language switcher */}
          <div className="relative">
            <button
              data-testid="lang-switcher"
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1 font-mono text-[0.7rem] uppercase tracking-widest text-charcoal/70 hover:text-brass transition-colors"
            >
              {languages.find((l) => l.code === lang)?.label}
              <CaretDown size={12} weight="thin" />
            </button>
            {langOpen && (
              <div
                data-testid="lang-menu"
                className="absolute right-0 mt-3 w-36 bg-ivory border border-habitus-border"
              >
                {languages.map((l) => (
                  <button
                    key={l.code}
                    data-testid={`lang-option-${l.code}`}
                    onClick={() => {
                      setLang(l.code);
                      setLangOpen(false);
                    }}
                    className={`block w-full text-left px-4 py-3 font-mono text-[0.7rem] uppercase tracking-widest hover:bg-habitus-muted transition-colors ${
                      lang === l.code ? "text-brass" : "text-charcoal/70"
                    }`}
                  >
                    {l.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            data-testid="header-cta"
            onClick={onCta}
            className="hidden sm:inline-block bg-charcoal text-ivory hover:bg-secondary transition-colors duration-300 px-6 py-3 font-mono uppercase tracking-widest text-[0.7rem]"
          >
            {t.nav.requestInvitation}
          </button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMenuOpen((v) => !v)}
            className="lg:hidden font-mono text-xs uppercase tracking-widest"
          >
            {menuOpen ? "×" : "≡"}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div data-testid="mobile-menu" className="lg:hidden bg-ivory border-t border-habitus-border px-6 py-6 flex flex-col gap-4">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className="text-left font-mono text-xs uppercase tracking-widest text-charcoal/70"
            >
              {item.label}
            </button>
          ))}
          <button
            onClick={() => {
              setMenuOpen(false);
              onCta();
            }}
            className="text-left font-mono text-xs uppercase tracking-widest text-brass"
          >
            {t.nav.requestInvitation}
          </button>
        </div>
      )}
    </header>
  );
}
